import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import {
  Button,
  Tag,
  InlineNotification,
  SkeletonPlaceholder,
  SkeletonText,
  Dropdown,
  TextInput,
  Select,
  SelectItem,
  FileUploader,
  Checkbox,
} from '@carbon/react';
import {
  ChevronLeft,
  ChevronRight,
  Time,
  Location,
  User,
  Calendar,
  Document,
  Activity,
  Task,
  Checkmark,
  Add,
  Close,
  TrashCan,
  Edit,
  Copy,
  Filter,
} from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import { getApiErrorMessage } from '../utils/apiErrors';
import NativeDatePicker from '../components/NativeDatePicker';
import EmployeeTasksTab from '../components/EmployeeTasksTab';
import './EmployeeProfile.css';

// Pri generisanju korisničkog imena uklanjamo dijakritike (š, đ, ć, č, ž...) i
// zamenjujemo ih ASCII slovima (s, d, c, c, z). Npr. "Đorđe.Čolović" -> "dordje.colovic".
const foldDiacritics = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // skini kvačice: č->c, ć->c, š->s, ž->z
    .replace(/đ/g, 'd') // đ se ne razlaže preko NFD, pa ga menjamo ručno
    .replace(/Đ/g, 'd');

// ===========================================
// SVG Icon Components (consistent with ResidentProfile)
// ===========================================
const Icons = {
  ArrowLeft: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  ),
  User: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Calendar: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  FileText: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  Activity: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
    </svg>
  ),
  Task: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4"/>
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  ),
  Clock: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12,6 12,12 16,14"/>
    </svg>
  ),
  MapPin: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  CheckCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M22 4L12 14.01l-3-3"/>
    </svg>
  ),
  Save: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
      <polyline points="17,21 17,13 7,13 7,21"/>
      <polyline points="7,3 7,8 15,8"/>
    </svg>
  ),
  Info: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="16" x2="12" y2="12"/>
      <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
  ),
  Pencil: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
    </svg>
  ),
  Mail: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  Phone: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  X: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  MoreHorizontal: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <circle cx="5" cy="12" r="2"/>
      <circle cx="12" cy="12" r="2"/>
      <circle cx="19" cy="12" r="2"/>
    </svg>
  ),
  TrashCan: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
      <line x1="10" y1="11" x2="10" y2="17"/>
      <line x1="14" y1="11" x2="14" y2="17"/>
    </svg>
  ),
  MinusCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="8" y1="12" x2="16" y2="12"/>
    </svg>
  ),
  Key: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  ),
  Shield: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  ),
};

// ===========================================
// Tab Configuration
// ===========================================
const TABS = [
  { id: 'general', label: 'Opšte informacije', icon: Icons.Info },
  { id: 'permissions', label: 'Dozvole', icon: Icons.Shield },
  { id: 'shifts', label: 'Smene', icon: Icons.Calendar },
  { id: 'tasks', label: 'Zadaci', icon: Icons.Task },
  { id: 'activity', label: 'Aktivnost', icon: Icons.Activity },
];

// ===========================================
// Utility Functions
// ===========================================
const formatDateKey = (date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const formatTime = (dateString) => {
  if (!dateString) return '-';
  // Backend šalje vreme bez vremenske zone (npr. "2026-09-30T06:00:00").
  // Čitamo sate i minute direktno iz stringa kako bismo prikazali tačno ono
  // što backend šalje, bez konverzije u lokalnu/UTC vremensku zonu.
  const match = String(dateString).match(/T(\d{2}):(\d{2})/);
  if (match) {
    return `${match[1]}:${match[2]}`;
  }
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const formatDateLong = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('sr-Latn', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

const getMonthName = (date) => {
  const months = ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];
  return months[date.getMonth()];
};

const getDayName = (date) => {
  const days = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
  return days[date.getDay()];
};

const getShortDayName = (date) => {
  const days = ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'];
  return days[date.getDay()];
};

const translateStatus = (status) => {
  const statusMap = {
    'planned': 'Planirana',
    'in-progress': 'U toku',
    'completed': 'Završena',
  };
  return statusMap[status] || status;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'planned': return 'blue';
    case 'in-progress': return 'green';
    case 'completed': return 'gray';
    default: return 'purple';
  }
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase();
};

// ===========================================
// Image Modal Component (consistent with ResidentProfile)
// ===========================================
const ImageModal = ({ isOpen, onClose, imageSrc, altText }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="emp-image-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Uvećana fotografija"
    >
      <div className="emp-image-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="emp-image-modal__close"
          onClick={onClose}
          aria-label="Zatvori"
        >
          <Icons.X />
        </button>
        <img
          src={imageSrc}
          alt={altText}
          className="emp-image-modal__image"
        />
      </div>
    </div>
  );
};

// ===========================================
// Avatar Component
// ===========================================
const EmployeeAvatar = ({ avatar, fullName, onClick, clickable = false }) => {
  const [imageError, setImageError] = useState(false);

  const handleClick = () => {
    if (clickable && onClick && avatar && !imageError) {
      onClick();
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && clickable && onClick && avatar && !imageError) {
      e.preventDefault();
      onClick();
    }
  };

  if (avatar && !imageError) {
    return (
      <img
        src={avatar}
        alt={`Fotografija: ${fullName}`}
        className={`emp-avatar ${clickable ? 'emp-avatar--clickable' : ''}`}
        onError={() => setImageError(true)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={clickable ? 0 : undefined}
        role={clickable ? 'button' : undefined}
        aria-label={clickable ? `Prikaži fotografiju: ${fullName}` : undefined}
      />
    );
  }

  return (
    <div className="emp-avatar emp-avatar--placeholder" aria-hidden="true">
      {getInitials(fullName)}
    </div>
  );
};

// ===========================================
// Overflow Menu Component (three-dots menu, consistent with ResidentProfile)
// ===========================================
const OverflowMenu = ({ isOpen, onToggle, onClose, menuRef, children }) => {
  const menuItemsRef = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const handleKeyDown = (e) => {
    const items = menuItemsRef.current.filter(Boolean);

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        onClose?.();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev - 1 + items.length) % items.length);
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(items.length - 1);
        break;
      case 'Tab':
        onClose?.();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && menuItemsRef.current[focusedIndex]) {
      menuItemsRef.current[focusedIndex].focus();
    }
  }, [focusedIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  return (
    <div className="emp-overflow-menu" ref={menuRef}>
      <button
        className="emp-overflow-trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Više opcija"
      >
        <Icons.MoreHorizontal />
      </button>
      {isOpen && (
        <div
          className="emp-overflow-dropdown"
          role="menu"
          aria-label="Dodatne akcije"
          onKeyDown={handleKeyDown}
        >
          {/* `toArray` + `isValidElement` uklanja null/false decu (uslovno
              renderovane stavke). React.Children.map poziva callback i za njih,
              pa bi `cloneElement(null, ...)` pukao. */}
          {React.Children.toArray(children)
            .filter((child) => React.isValidElement(child))
            .map((child, index) =>
              React.cloneElement(child, {
                ref: (el) => (menuItemsRef.current[index] = el),
                tabIndex: focusedIndex === index ? 0 : -1
              })
            )}
        </div>
      )}
    </div>
  );
};

const OverflowMenuItem = React.forwardRef(({ icon: Icon, children, onClick, variant = 'default', tabIndex = -1, disabled = false }, ref) => (
  <button
    ref={ref}
    className={`emp-overflow-item ${variant === 'danger' ? 'emp-overflow-item--danger' : ''}`}
    onClick={onClick}
    role="menuitem"
    tabIndex={tabIndex}
    disabled={disabled}
    aria-disabled={disabled}
  >
    {Icon && <span className="emp-overflow-item__icon"><Icon /></span>}
    <span className="emp-overflow-item__label">{children}</span>
  </button>
));
OverflowMenuItem.displayName = 'OverflowMenuItem';

// ===========================================
// Shift Calendar Component (Smene tab)
// ===========================================
const viewOptions = [
  { id: 'month', label: 'Mesečni' },
  { id: 'week', label: 'Nedeljni' },
  { id: 'day', label: 'Dnevni' },
];

const ShiftCalendar = ({ shifts, loading, error, selectedDates, onToggleDate, onDeleteShift, onSelectDay, readOnly = false }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100' || theme === 'g90';
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isFilterExiting, setIsFilterExiting] = useState(false);

  const handleCloseMobileFilter = () => {
    setIsFilterExiting(true);
    setTimeout(() => {
      setIsMobileFilterOpen(false);
      setIsFilterExiting(false);
    }, 300);
  };

  const getShiftsForDate = (date) => {
    const dateKey = formatDateKey(date);
    return shifts.filter((shift) => {
      // Backend šalje datum bez vremenske zone (npr. "2026-09-30T06:00:00").
      // Čitamo datum direktno iz stringa kako bismo smenu prikazali na dan koji je backend odredio.
      const match = String(shift.startTime).match(/^(\d{4})-(\d{2})-(\d{2})/);
      const shiftDateKey = match
        ? `${match[1]}-${match[2]}-${match[3]}`
        : formatDateKey(new Date(shift.startTime));
      return shiftDateKey === dateKey;
    });
  };

  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d > today;
  };

  const isDateSelected = (date) => {
    return selectedDates.some((d) => formatDateKey(d) === formatDateKey(date));
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + direction * 7);
    } else if (view === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  // Month view
  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const days = [];
    const tempDate = new Date(year, month, 1);

    while (tempDate.getMonth() === month) {
      const dateShifts = getShiftsForDate(tempDate);
      const isToday = formatDateKey(tempDate) === formatDateKey(new Date());
      days.push({
        date: new Date(tempDate),
        dateKey: formatDateKey(tempDate),
        isToday,
        shifts: dateShifts,
      });
      tempDate.setDate(tempDate.getDate() + 1);
    }

    const firstDayOfWeek = firstDay.getDay();
    const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    const allDays = ['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'];
    const rotatedDays = [...allDays.slice(offset), ...allDays.slice(0, offset)];

    return (
      <div className="emp-calendar-month-view">
        <div className="emp-calendar-month-header">
          {rotatedDays.map((day) => (
            <div key={day} className="emp-calendar-weekday-label">{day}</div>
          ))}
        </div>
        <div className="emp-calendar-month-grid">
          {days.map((day, idx) => {
            const selected = isDateSelected(day.date);
            const future = isFutureDate(day.date);
            return (
            <div
              key={idx}
              className={`emp-calendar-day-cell ${day.isToday ? 'today' : ''} ${day.shifts.length > 0 ? 'has-shifts' : ''} ${selected ? 'selected' : ''}`}
              onClick={() => {
                if (day.shifts.length > 0) {
                  onSelectDay(day);
                } else {
                  setCurrentDate(day.date);
                }
              }}
            >
              <div className="emp-calendar-day-number">{day.date.getDate()}</div>
              {day.shifts.length > 0 && (
                <div className="emp-calendar-day-shifts">
                  {day.shifts.slice(0, 2).map((shift, sIdx) => (
                    <div key={sIdx} className="emp-calendar-shift-chip">
                      <span className="emp-calendar-shift-chip__time">
                        {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                      </span>
                    </div>
                  ))}
                  {day.shifts.length > 2 && (
                    <span className="emp-calendar-shift-more">+{day.shifts.length - 2}</span>
                  )}
                </div>
              )}
              {day.shifts.length === 1 && future && (
                <button
                  type="button"
                  className="emp-calendar-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteShift(day.shifts[0]);
                  }}
                  title={readOnly ? 'Zaposleni je deaktiviran' : 'Obriši smenu'}
                  disabled={readOnly}
                >
                  <TrashCan size={16} />
                </button>
              )}
              {future && day.shifts.length === 0 && (
                <button
                  type="button"
                  className={`emp-calendar-add-btn ${selected ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleDate(day.date);
                  }}
                  title={readOnly ? 'Zaposleni je deaktiviran' : 'Dodaj smenu'}
                  disabled={readOnly}
                >
                  {selected ? <Checkmark size={16} /> : <Add size={16} />}
                </button>
              )}
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Week view
  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    const dayOfWeek = currentDate.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startOfWeek.setDate(currentDate.getDate() + diff);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDays.push({
        date,
        dateKey: formatDateKey(date),
        isToday: formatDateKey(date) === formatDateKey(new Date()),
        shifts: getShiftsForDate(date),
      });
    }

    return (
      <div className="emp-calendar-week-view">
        <div className="emp-calendar-week-grid">
          {weekDays.map((day, idx) => {
            const selected = isDateSelected(day.date);
            const future = isFutureDate(day.date);
            return (
            <div
              key={idx}
              className={`emp-calendar-week-day-column ${day.isToday ? 'today' : ''} ${selected ? 'selected' : ''}`}
              onClick={() => {
                if (day.shifts.length > 0) onSelectDay(day);
              }}
            >
              <div className="emp-week-day-header">
                <div className="emp-week-day-name">{getShortDayName(day.date)}</div>
                <div className="emp-week-day-number">{day.date.getDate()}</div>
              </div>
              {day.shifts.length === 0 ? (
                <div className="emp-free-day-message">Slobodan dan</div>
              ) : (
                day.shifts.map((shift, sIdx) => (
                  <div key={sIdx} className="emp-week-shift-card">
                    <div className="emp-week-shift-time">
                      {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                    </div>
                    <div className="emp-week-shift-name">{shift.name}</div>
                    <div className="emp-week-shift-area">{shift.area}</div>
                  </div>
                ))
              )}
              {day.shifts.length === 1 && future && (
                <button
                  type="button"
                  className="emp-calendar-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteShift(day.shifts[0]);
                  }}
                  title={readOnly ? 'Zaposleni je deaktiviran' : 'Obriši smenu'}
                  disabled={readOnly}
                >
                  <TrashCan size={16} />
                </button>
              )}
              {future && day.shifts.length === 0 && (
                <button
                  type="button"
                  className={`emp-calendar-add-btn ${selected ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleDate(day.date);
                  }}
                  title={readOnly ? 'Zaposleni je deaktiviran' : 'Dodaj smenu'}
                  disabled={readOnly}
                >
                  {selected ? <Checkmark size={16} /> : <Add size={16} />}
                </button>
              )}
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Day view
  const renderDayView = () => {
    const dayShifts = getShiftsForDate(currentDate);

    return (
      <div className="emp-calendar-day-view">
        <div className="emp-calendar-day-shifts-list">
          {dayShifts.length === 0 ? (
            <div className="emp-no-shifts-message">
              <Time size={32} />
              <p>Nema smena za ovaj dan</p>
            </div>
          ) : (
            dayShifts.map((shift, idx) => (
              <div key={idx} className="emp-day-shift-card">
                <div className="emp-shift-time-indicator">
                  <div className="emp-time-dot" />
                  <div className="emp-time-line" />
                </div>
                <div className="emp-shift-content">
                  <div className="emp-shift-header">
                    <div className="emp-shift-title-row">
                      <h5>{shift.name}</h5>
                      <Tag size="sm" type={getStatusColor(shift.status)}>{translateStatus(shift.status)}</Tag>
                    </div>
                    <div className="emp-shift-time-range">
                      <Time size={16} />
                      {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                    </div>
                  </div>
                  <div className="emp-shift-details">
                    <div className="emp-shift-detail-row">
                      <Location size={16} />
                      <span className="emp-shift-detail-label">Oblast:</span>
                      <span className="emp-shift-detail-value">{shift.area || '-'}</span>
                    </div>
                    {shift.description && (
                      <div className="emp-shift-description">
                        <span className="emp-shift-detail-label">Opis:</span>
                        <p>{shift.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="emp-shift-calendar">
      {/* Header */}
      <div className="emp-calendar-header">
        <div className="emp-calendar-header-left">
          <h3 className="emp-calendar-title">
            {view === 'month' && `${getMonthName(currentDate)} ${currentDate.getFullYear()}`}
            {view === 'week' && `Nedelja ${currentDate.getDate()} ${getMonthName(currentDate)}`}
            {view === 'day' && `${getDayName(currentDate)}, ${currentDate.getDate()} ${getMonthName(currentDate)}`}
          </h3>
        </div>
        <div className="emp-calendar-header-right">
          <Button
            kind="ghost"
            size="sm"
            renderIcon={ChevronLeft}
            onClick={() => navigateDate(-1)}
            hasIconOnly
            iconDescription="Prethodni"
          />
          <Button
            kind="ghost"
            size="sm"
            onClick={goToToday}
          >
            Danas
          </Button>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={ChevronRight}
            onClick={() => navigateDate(1)}
            hasIconOnly
            iconDescription="Sledeći"
          />
          <div className="emp-calendar-view-selector">
            <Dropdown
              id="emp-view-selector"
              size="sm"
              titleText=""
              label=""
              items={viewOptions}
              itemToString={(item) => (item ? item.label : '')}
              selectedItem={viewOptions.find((option) => option.id === view)}
              onChange={({ selectedItem }) => setView(selectedItem.id)}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="emp-calendar-content">
        {loading ? (
          <div className="emp-calendar-skeleton">
            <div className="emp-calendar-month-header">
              {['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'].map((day) => (
                <div key={day} className="emp-calendar-weekday-label">{day}</div>
              ))}
            </div>
            <div className="emp-calendar-month-grid">
              {Array.from({ length: 35 }).map((_, idx) => (
                <SkeletonPlaceholder key={idx} className="emp-calendar-day-skeleton" />
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="emp-no-shifts-message">
            <Time size={32} />
            <p>{error}</p>
          </div>
        ) : (
          <>
            {view === 'month' && renderMonthView()}
            {view === 'week' && renderWeekView()}
            {view === 'day' && renderDayView()}
          </>
        )}
      </div>

      {/* Mobile Filter Button */}
      {ReactDOM.createPortal(
        <button
          className="floating-button emp-mobile-filter-floating-btn"
          onClick={() => setIsMobileFilterOpen(true)}
          style={{
            position: 'fixed',
            bottom: '2rem',
            left: '2rem',
            height: '56px',
            padding: '0 24px',
            backgroundColor: '#9fb746',
            border: 'none',
            borderRadius: '28px',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: '12px',
            outline: 'none',
            zIndex: 9998
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#8da63d';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#9fb746';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
          }}
        >
          <Filter
            size={24}
            style={{
              flexShrink: 0,
              minWidth: '24px',
              minHeight: '24px',
              fill: '#ffffff'
            }}
            aria-label="Filteri"
            color="#ffffff"
          />
          <span style={{
            color: 'white',
            fontWeight: '500',
            fontSize: '0.95rem',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            Filteri
          </span>
        </button>,
        document.body
      )}

      {/* Mobile Filter Modal */}
      {isMobileFilterOpen && ReactDOM.createPortal(
        <div
          className={isFilterExiting ? 'filter-modal-exiting' : ''}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDarkMode ? 'rgba(22, 22, 22, 0.85)' : 'rgba(244, 244, 244, 0.85)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-end',
            animation: isFilterExiting ? 'empFadeOut 0.2s ease-out' : 'empFadeIn 0.2s ease-out'
          }}
          onClick={handleCloseMobileFilter}
        >
          <div
            className="emp-mobile-filter-panel"
            data-carbon-theme={theme}
            style={{
              width: '100%',
              backgroundColor: isDarkMode ? '#262626' : '#ffffff',
              borderRadius: '16px 16px 0 0',
              padding: '1.5rem',
              animation: isFilterExiting ? 'empSlideDownOut 0.3s ease-out' : 'empSlideUpIn 0.3s ease-out',
              maxHeight: '70vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '500', color: isDarkMode ? '#ffffff' : '#161616' }}>Filteri</h3>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                className="emp-mobile-filter-close-btn"
                renderIcon={Close}
                iconDescription="Zatvori"
                onClick={handleCloseMobileFilter}
                style={{ color: isDarkMode ? '#ffffff' : '#161616' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'nowrap', width: '100%' }}>
                <Button
                  kind="primary"
                  size="sm"
                  renderIcon={ChevronLeft}
                  onClick={() => navigateDate(-1)}
                  hasIconOnly
                  iconDescription="Prethodni"
                  style={{ backgroundColor: '#9fb746', borderColor: '#9fb746', flex: 1, minWidth: '48px' }}
                />
                <Button
                  kind="primary"
                  size="sm"
                  className="emp-mobile-today-btn"
                  onClick={goToToday}
                  style={{ backgroundColor: '#9fb746', borderColor: '#9fb746', whiteSpace: 'nowrap', flex: 1, justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
                >
                  Danas
                </Button>
                <Button
                  kind="primary"
                  size="sm"
                  renderIcon={ChevronRight}
                  onClick={() => navigateDate(1)}
                  hasIconOnly
                  iconDescription="Sledeći"
                  style={{ backgroundColor: '#9fb746', borderColor: '#9fb746', flex: 1, minWidth: '48px' }}
                />
              </div>

              <div style={{ textAlign: 'center', fontSize: '0.875rem', fontWeight: '500', color: isDarkMode ? '#ffffff' : '#161616' }}>
                {view === 'month' && `${getMonthName(currentDate)} ${currentDate.getFullYear()}`}
                {view === 'week' && `Nedelja ${currentDate.getDate()} ${getMonthName(currentDate)}`}
                {view === 'day' && `${getDayName(currentDate)}, ${currentDate.getDate()} ${getMonthName(currentDate)}`}
              </div>

              <Dropdown
                id="emp-mobile-view-selector"
                size="md"
                titleText="Prikaz"
                label="Prikaz"
                items={viewOptions}
                itemToString={(item) => (item ? item.label : '')}
                selectedItem={viewOptions.find((option) => option.id === view)}
                onChange={({ selectedItem }) => setView(selectedItem.id)}
              />

              <Button
                kind="primary"
                size="md"
                style={{ width: '100%', marginTop: '1rem', backgroundColor: '#9fb746', borderColor: '#9fb746' }}
                onClick={handleCloseMobileFilter}
              >
                Zatvori
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ===========================================
// Placeholder Tab Component (for tabs not yet implemented)
// ===========================================
const PlaceholderTab = ({ icon: Icon, title, description }) => (
  <div className="emp-placeholder-tab">
    <div className="emp-placeholder-icon">
      <Icon />
    </div>
    <h3 className="emp-placeholder-title">{title}</h3>
    <p className="emp-placeholder-description">{description}</p>
  </div>
);

// ===========================================
// General Info Tab Component (Opšte informacije)
// ===========================================
const GeneralInfo = ({ employee, onEditSection, onRegister, onResetPassword, isRegistering, isResetting, accountResult, onCopyCode, copied, readOnly = false }) => {
  const fullName = employee.fullName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
  const role = employee.employeeTypeName || employee.role || '-';

  const formatHireDate = (dateString) => {
    if (!dateString || dateString.startsWith('0001-01-01')) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return new Intl.DateTimeFormat('sr-Latn', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const formatExpiry = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
  };

  const isRegistered = employee.isRegistered === true;

  return (
    <div className="emp-general-info">
      <div className="emp-cards-grid">
        <div className="emp-card">
          <div className="emp-card__header">
            <h3 className="emp-card__title">
              <span className="emp-card__title-icon"><Icons.User /></span>
              Identitet
            </h3>
            <div className="emp-card__actions">
              <button
                type="button"
                className="emp-card__edit-btn"
                onClick={() => onEditSection('identity')}
                aria-label="Izmeni identitet"
                title={readOnly ? 'Zaposleni je deaktiviran' : 'Izmeni'}
                disabled={readOnly}
              >
                <Icons.Pencil />
              </button>
            </div>
          </div>
          <div className="emp-card__body">
            <div className="emp-data-list">
              <div className="emp-data-item">
                <span className="emp-data-label">Ime i prezime</span>
                <span className="emp-data-value">{fullName || '-'}</span>
              </div>
              <div className="emp-data-item">
                <span className="emp-data-label">Tip zaposlenog</span>
                <span className="emp-data-value">{role}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="emp-card">
          <div className="emp-card__header">
            <h3 className="emp-card__title">
              <span className="emp-card__title-icon"><Icons.Mail /></span>
              Kontakt
            </h3>
            <div className="emp-card__actions">
              <button
                type="button"
                className="emp-card__edit-btn"
                onClick={() => onEditSection('contact')}
                aria-label="Izmeni kontakt"
                title={readOnly ? 'Zaposleni je deaktiviran' : 'Izmeni'}
                disabled={readOnly}
              >
                <Icons.Pencil />
              </button>
            </div>
          </div>
          <div className="emp-card__body">
            <div className="emp-data-list">
              <div className="emp-data-item">
                <span className="emp-data-label">Email</span>
                <span className="emp-data-value">{employee.email || '-'}</span>
              </div>
              <div className="emp-data-item">
                <span className="emp-data-label">Telefon</span>
                <span className="emp-data-value">{employee.phone || employee.phoneNumber || '-'}</span>
              </div>
              <div className="emp-data-item">
                <span className="emp-data-label">Adresa</span>
                <span className="emp-data-value">{employee.address || '-'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="emp-card">
          <div className="emp-card__header">
            <h3 className="emp-card__title">
              <span className="emp-card__title-icon"><Icons.Calendar /></span>
              Zaposlenje
            </h3>
            <div className="emp-card__actions">
              <button
                type="button"
                className="emp-card__edit-btn"
                onClick={() => onEditSection('employment')}
                aria-label="Izmeni zaposlenje"
                title={readOnly ? 'Zaposleni je deaktiviran' : 'Izmeni'}
                disabled={readOnly}
              >
                <Icons.Pencil />
              </button>
            </div>
          </div>
          <div className="emp-card__body">
            <div className="emp-data-list">
              <div className="emp-data-item">
                <span className="emp-data-label">Datum zaposlenja</span>
                <span className="emp-data-value">{formatHireDate(employee.hireDate || employee.startDate)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="emp-card">
          <div className="emp-card__header">
            <h3 className="emp-card__title">
              <span className="emp-card__title-icon"><Icons.Key /></span>
              Nalog
            </h3>
          </div>
          <div className="emp-card__body emp-card__body--scroll">
            <div className="emp-data-list">
              {isRegistered ? (
                <>
                  <div className="emp-data-item">
                    <span className="emp-data-label">Korisničko ime</span>
                    <span className="emp-data-value emp-data-value--row">
                      <span className="emp-account-username">{employee.username || '-'}</span>
                      <span className="emp-account-status emp-account-status--registered">
                        <Icons.CheckCircle />
                        Registrovan
                      </span>
                    </span>
                  </div>

                  {/* Show generated secure code after registration / reset */}
                  {accountResult && (
                    <div className="emp-account-code emp-account-code--inline">
                      <span className="emp-account-code__label">Sigurnosni kod</span>
                      <div className="emp-account-code__box">
                        <span className="emp-account-code__value">{accountResult.secureCode}</span>
                        <button
                          type="button"
                          className="emp-account-code__copy"
                          onClick={onCopyCode}
                          aria-label="Kopiraj kod"
                          title="Kopiraj kod"
                        >
                          {copied ? <Checkmark size={18} /> : <Copy size={18} />}
                        </button>
                      </div>
                      {copied && (
                        <span className="emp-account-code__copied">Kod je kopiran!</span>
                      )}
                      <div className="emp-account-result__expiry">
                        <Icons.Clock />
                        <span>
                          Ističe u <strong>{formatExpiry(accountResult.secureCodeExpiresAt)}</strong>
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="emp-account-actions">
                    <button
                      type="button"
                      className="emp-account-btn emp-account-btn--secondary"
                      onClick={onResetPassword}
                      disabled={isResetting || readOnly}
                    >
                      {isResetting ? 'Generisanje...' : 'Reset lozinke'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="emp-data-item">
                    <span className="emp-data-label">Status</span>
                    <span className="emp-data-value">
                      <span className="emp-account-status emp-account-status--unregistered">
                        <Icons.Info />
                        Nije registrovan
                      </span>
                    </span>
                  </div>
                  <div className="emp-account-actions">
                    <button
                      type="button"
                      className="emp-account-btn emp-account-btn--primary"
                      onClick={onRegister}
                      disabled={isRegistering || readOnly}
                    >
                      {isRegistering ? 'Registracija...' : 'Registruj'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// Permissions Tab Component (Dozvole)
// ===========================================

// Nazivi kategorija (prvi segment ključa dozvole)
const PERMISSION_CATEGORY_LABELS = {
  rooms: 'Sobe',
  areas: 'Oblasti',
  groups: 'Grupe',
  activities: 'Aktivnosti',
  'resident-notes': 'Beleške o korisniku',
  'action-needed': 'Akcije koje treba preduzeti',
  employees: 'Zaposleni',
  'employee-types': 'Tipovi zaposlenih',
  accounts: 'Nalozi',
  shifts: 'Smene',
  'shift-types': 'Tipovi smena',
  tasks: 'Zadaci',
  residents: 'Korisnici',
  'diet-plans': 'Planovi ishrane',
  institution: 'Ustanova',
  'resident-budgets': 'Budžet korisnika',
  menus: 'Meniji',
  meals: 'Obroci',
};

// Dozvole koje se po ključu razlikuju od grupe u kojoj se prikazuju.
// Sve navedene dozvole su vezane za profil korisnika, pa se prikazuju u grupi
// „Korisnici" (kao i `residents.*`), a ne kao zasebne grupe ispred nje:
//   - `resident-activities.*` -> „Aktivnosti korisnika"
//   - `documents.*`           -> „Pregled dokumenata" / „Upload dokumenata"
//   - `budget.*`              -> „Trošak/depozit za rezidentski budžet"
//   - `supplies.*`            -> „Pravljenje, menjanje i brisanje zaliha za rezidenta"
const PERMISSION_CATEGORY_GROUPING = {
  'resident-activities': 'residents',
  documents: 'residents',
  budget: 'residents',
  supplies: 'residents',
};

const getPermissionCategory = (key) => {
  const prefix = String(key).split('.')[0];
  const categoryKey = PERMISSION_CATEGORY_GROUPING[prefix] || prefix;
  return PERMISSION_CATEGORY_LABELS[categoryKey] || 'Ostalo';
};

// Normalizuje `effective` iz API-ja u niz { key, label, granted }.
// Backend vraća niz objekata: [{ permission, name, label, isAllowed }].
// Radi i sa starim formatom (mapa { key: boolean }) radi bezbednosti.
const normalizeEffective = (effective) => {
  if (!effective) return [];
  if (Array.isArray(effective)) {
    return effective
      .filter((item) => item && item.permission)
      .map((item) => ({
        key: item.permission,
        label: item.label || item.name || item.permission,
        granted: item.isAllowed === true,
      }));
  }
  if (typeof effective === 'object') {
    return Object.entries(effective).map(([key, value]) => ({
      key,
      label: key,
      granted: value === true,
    }));
  }
  return [];
};

// Zadržava poznate labele kada backend u PUT odgovoru vrati `effective`
// bez `label`/`name` polja. Bez ovoga bi se posle čuvanja prikazao sirovi
// ključ dozvole (npr. "groups.activities.manage") umesto čitljivog naziva.
const mergeEffectiveLabels = (nextEffective, prevEffective) => {
  const labelByKey = new Map();
  normalizeEffective(prevEffective).forEach((item) => {
    if (item.label && item.label !== item.key) labelByKey.set(item.key, item.label);
  });

  return (Array.isArray(nextEffective) ? nextEffective : []).map((item) => {
    if (!item || !item.permission) return item;
    // Ako odgovor već nosi labelu, ne diraj ga.
    if (item.label || item.name) return item;
    const known = labelByKey.get(item.permission);
    return known ? { ...item, label: known } : item;
  });
};

// Grupiše dozvole po kategorijama (prvi segment ključa)
const buildPermissionGroups = (effective) => {
  const items = normalizeEffective(effective);
  const groups = new Map();
  items.forEach((item) => {
    const category = getPermissionCategory(item.key);
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(item);
  });
  return Array.from(groups.entries()).map(([category, groupItems]) => ({
    category,
    items: groupItems.sort((a, b) => a.label.localeCompare(b.label, 'sr-Latn')),
  }));
};

const PermissionsTab = ({ employee, permissions, loading, error, onRegister, isRegistering, onSave, isSaving, readOnly = false }) => {
  const isRegistered = employee?.isRegistered === true;

  // Lokalna (radna) kopija dozvola koju korisnik menja čekiranjem
  const [draft, setDraft] = useState({});
  const [baseline, setBaseline] = useState({});

  // Sinhronizuj radnu kopiju kada se učitaju dozvole sa servera.
  // `effective` je niz objekata: [{ permission, name, label, isAllowed }].
  useEffect(() => {
    const items = normalizeEffective(permissions?.effective);
    if (items.length > 0) {
      const next = {};
      items.forEach((item) => {
        next[item.key] = item.granted;
      });
      setDraft(next);
      setBaseline(next);
    }
  }, [permissions]);

  // Ima izmena samo ako se radna kopija razlikuje od početnog stanja
  const isDirty = Object.keys(draft).some((key) => draft[key] !== baseline[key]);

  // Na mobilnom: dok je prikazano lebdeće dugme za čuvanje, sakrij
  // "scroll to top" strelicu (oba su FAB-ovi na istoj strani i smetaju jedan drugom).
  useEffect(() => {
    const active = isDirty;
    document.body.classList.toggle('emp-permissions--save-active', active);
    return () => {
      document.body.classList.remove('emp-permissions--save-active');
    };
  }, [isDirty]);

  const handleToggle = (key, checked) => {
    // Deaktiviran zaposleni - dozvole se ne mogu menjati
    if (readOnly) return;
    setDraft((prev) => ({ ...prev, [key]: checked }));
  };

  const handleSave = () => {
    if (onSave) onSave(draft);
  };

  // Korisnik mora prvo da se registruje da bi imao dozvole
  if (!isRegistered) {
    return (
      <div className="emp-permissions">
        <div className="emp-empty-state">
          <div className="emp-empty-icon"><Icons.Shield /></div>
          <h2 className="emp-empty-title">Korisnik nije registrovan</h2>
          <p className="emp-empty-description">
            Da biste videli dozvole, korisnik prvo mora da se registruje.
          </p>
          <div className="emp-account-actions emp-permissions__actions">
            <button
              type="button"
              className="emp-account-btn emp-account-btn--primary"
              onClick={onRegister}
              disabled={isRegistering || readOnly}
            >
              {isRegistering ? 'Registracija...' : 'Registruj korisnika'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="emp-permissions">
        <div className="emp-card">
          <div className="emp-card__header">
            <h3 className="emp-card__title">
              <span className="emp-card__title-icon"><Icons.Shield /></span>
              Dozvole pristupa
            </h3>
            <div className="emp-card__actions">
              <SkeletonText className="emp-permissions-skeleton-count" />
            </div>
          </div>
          <div className="emp-card__body">
            <div className="emp-permissions-groups">
              {[0, 1, 2].map((g) => (
                <div key={g} className="emp-permissions-group">
                  <SkeletonText className="emp-permissions-skeleton-title" />
                  <div className="emp-permissions-grid">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="emp-permission-check emp-permission-check--skeleton">
                        <SkeletonPlaceholder className="emp-permission-skeleton-box" />
                        <SkeletonText className="emp-permission-skeleton-label" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="emp-permissions">
        <div className="emp-empty-state">
          <div className="emp-empty-icon"><Icons.Info /></div>
          <h2 className="emp-empty-title">Greška</h2>
          <p className="emp-empty-description">{error}</p>
        </div>
      </div>
    );
  }

  const groups = buildPermissionGroups(permissions?.effective);
  const total = groups.reduce((sum, g) => sum + g.items.length, 0);
  const grantedCount = Object.values(draft).filter(Boolean).length;

  return (
    <div className="emp-permissions">
      <div className="emp-card">
        <div className="emp-card__header">
          <h3 className="emp-card__title">
            <span className="emp-card__title-icon"><Icons.Shield /></span>
            Dozvole pristupa
          </h3>
          <div className="emp-card__actions">
            <span className="emp-permissions-count">
              {grantedCount} / {total} aktivno
            </span>
          </div>
        </div>
        <div className="emp-card__body">
          {total === 0 ? (
            <div className="emp-permissions-empty">
              <Icons.Shield />
              <p>Korisnik trenutno nema dodeljenih dozvola.</p>
            </div>
          ) : (
            <div className="emp-permissions-groups">
              {groups.map((group) => (
                <div key={group.category} className="emp-permissions-group">
                  <h4 className="emp-permissions-group__title">{group.category}</h4>
                  <div className="emp-permissions-grid">
                    {group.items.map((perm) => (
                      <div
                        key={perm.key}
                        className={`emp-permission-check ${draft[perm.key] ? 'emp-permission-check--on' : ''}`}
                      >
                        <Checkbox
                          id={`perm-${perm.key}`}
                          labelText={perm.label}
                          checked={!!draft[perm.key]}
                          onChange={(_, { checked }) => handleToggle(perm.key, checked)}
                          disabled={readOnly}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {total > 0 && isDirty && !readOnly && (
          <button
            type="button"
            className="emp-permissions-save-fab"
            onClick={handleSave}
            disabled={isSaving}
          >
            <Icons.Save />
            <span>{isSaving ? 'Čuvanje...' : 'Sačuvaj dozvole'}</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ===========================================
// Edit Sidebar Component - Right side panel for editing employee data
// ===========================================
const EmployeeEditSidebar = ({ isOpen, onClose, employee, onSave, isSaving, employeeTypes, section = 'identity' }) => {
  const [formData, setFormData] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);

  // Initialize form data when employee changes or sidebar opens
  useEffect(() => {
    if (employee && isOpen) {
      setFormData({
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        email: employee.email || '',
        phoneNumber: employee.phoneNumber || employee.phone || '',
        address: employee.address || '',
        hireDate: employee.hireDate && !employee.hireDate.startsWith('0001-01-01')
          ? employee.hireDate.split('T')[0]
          : '',
        employeeTypeId: employee.employeeTypeId || '',
        imgSrc: employee.imgSrc || '',
      });
      setImageFile(null);
    }
  }, [employee, isOpen]);

  // Upload a new image (replaces the old one) using the employee upload endpoint
  const uploadNewImage = async () => {
    if (!imageFile || !employee?.id) return null;
    setIsImageUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', imageFile);

      const uploadResponse = await axiosInstance.post(
        `/api/Employee/${employee.id}/upload-image`,
        uploadFormData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (uploadResponse.data?.success && uploadResponse.data?.data?.url) {
        return uploadResponse.data.data.url;
      }
      if (uploadResponse.data?.success && uploadResponse.data?.data?.imgSrc) {
        return uploadResponse.data.data.imgSrc;
      }
      return null;
    } catch (err) {
      console.error('Greška pri uploadu slike:', err);
      return null;
    } finally {
      setIsImageUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let updatedFormData = formData;
    if (imageFile) {
      const newImgSrc = await uploadNewImage();
      if (newImgSrc) {
        updatedFormData = { ...formData, imgSrc: newImgSrc };
      }
    }
    onSave(updatedFormData);
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      {isOpen && (
        <div className="emp-edit-sidebar-overlay" onClick={onClose} />
      )}
      <div className={`emp-edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="emp-edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            Izmeni zaposlenog
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={onClose}
          />
        </div>

        <div className="emp-edit-sidebar__content">
          <form className="emp-edit-sidebar__form" onSubmit={handleSubmit}>
            {(section === 'identity' || section === 'all') && (
              <>
                <TextInput
                  id="emp-edit-firstName"
                  labelText="Ime"
                  placeholder="Unesite ime"
                  value={formData.firstName || ''}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  disabled={isSaving}
                />
                <TextInput
                  id="emp-edit-lastName"
                  labelText="Prezime"
                  placeholder="Unesite prezime"
                  value={formData.lastName || ''}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  disabled={isSaving}
                />
              </>
            )}

            {(section === 'contact' || section === 'all') && (
              <>
                <TextInput
                  id="emp-edit-email"
                  labelText="Email"
                  placeholder="email@primer.com"
                  value={formData.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={isSaving}
                />
                <TextInput
                  id="emp-edit-phone"
                  labelText="Telefon"
                  placeholder="+381 ..."
                  value={formData.phoneNumber || ''}
                  onChange={(e) => handleChange('phoneNumber', e.target.value)}
                  disabled={isSaving}
                />
                <TextInput
                  id="emp-edit-address"
                  labelText="Adresa"
                  placeholder="Unesite adresu"
                  value={formData.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  disabled={isSaving}
                />
              </>
            )}

            {(section === 'employment' || section === 'all') && (
              <>
                <NativeDatePicker
                  id="emp-edit-hireDate"
                  labelText="Datum zaposlenja"
                  value={formData.hireDate ? new Date(formData.hireDate) : null}
                  onChange={(dates) => {
                    if (dates && dates[0]) {
                      const date = dates[0];
                      const formatted = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                      handleChange('hireDate', formatted);
                    } else {
                      handleChange('hireDate', '');
                    }
                  }}
                  disabled={isSaving}
                />
                <Select
                  id="emp-edit-employeeTypeId"
                  labelText="Tip zaposlenog"
                  value={formData.employeeTypeId || ''}
                  onChange={(e) => handleChange('employeeTypeId', e.target.value)}
                  disabled={isSaving}
                >
                  <SelectItem value="" text="Izaberite tip" />
                  {employeeTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id} text={type.name} />
                  ))}
                </Select>
              </>
            )}

            {(section === 'photo' || section === 'all') && (
              <>
                {/* Upload new image - replaces the old one */}
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: 'var(--cds-text-secondary)'
                  }}>
                    Nova fotografija (zamenjuje staru)
                  </label>
                  <FileUploader
                    labelTitle=""
                    labelDescription="Samo JPG, PNG, WEBP, GIF, BMP (maks 5MB)"
                    buttonLabel="Izaberi sliku"
                    filenameStatus="edit"
                    accept={['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp']}
                    multiple={false}
                    disabled={isSaving || isImageUploading}
                    onChange={(e) => setImageFile(e.target.files[0])}
                  />
                  {isImageUploading && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                      Upload slike u toku...
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="emp-edit-sidebar__actions">
              <Button
                kind="secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Otkaži
              </Button>
              <Button
                kind="primary"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? 'Čuvanje...' : 'Sačuvaj'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// ===========================================
// Account Sidebar Component - Right panel for registering employee / resetting password
// ===========================================
const AccountSidebar = ({ isOpen, onClose, employee, mode, onRegister, onResetPassword, isSubmitting }) => {
  const [username, setUsername] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Prefill username when opening in register mode
  useEffect(() => {
    if (isOpen && mode === 'register') {
      const firstName = foldDiacritics(employee?.firstName).trim().toLowerCase();
      const lastName = foldDiacritics(employee?.lastName).trim().toLowerCase();
      setUsername(firstName && lastName ? `${firstName}.${lastName}` : '');
      setResult(null);
      setCopied(false);
    }
  }, [isOpen, mode, employee]);

  // Reset result when switching modes
  useEffect(() => {
    if (isOpen) {
      setResult(null);
      setCopied(false);
    }
  }, [isOpen, mode]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleCopy = async () => {
    if (!result?.secureCode) return;
    try {
      await navigator.clipboard.writeText(result.secureCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Greška pri kopiranju:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register') {
      const res = await onRegister(username);
      if (res) setResult(res);
    } else {
      const res = await onResetPassword();
      if (res) setResult(res);
    }
  };

  const formatExpiry = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
  };

  const isRegisterMode = mode === 'register';

  return (
    <>
      {isOpen && (
        <div className="emp-account-sidebar-overlay" onClick={onClose} />
      )}
      <div className={`emp-account-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="emp-account-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            {isRegisterMode ? 'Registruj zaposlenog' : 'Reset lozinke'}
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={onClose}
            disabled={isSubmitting}
          />
        </div>

        <div className="emp-account-sidebar__content">
          {!result ? (
            <form className="emp-account-sidebar__form" onSubmit={handleSubmit}>
              {isRegisterMode ? (
                <>
                  <p className="emp-account-sidebar__hint">
                    Unesite korisničko ime za nalog zaposlenog. Nakon registracije, zaposleni će dobiti sigurnosni kod za postavljanje lozinke.
                  </p>
                  <TextInput
                    id="emp-account-username"
                    labelText="Korisničko ime"
                    placeholder="ime.prezime"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </>
              ) : (
                <p className="emp-account-sidebar__hint">
                  Generisaće se novi sigurnosni kod za zaposlenog <strong>{employee?.fullName || ''}</strong>. Stara lozinka će biti poništena.
                </p>
              )}

              <div className="emp-account-sidebar__actions">
                <Button
                  kind="secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Otkaži
                </Button>
                <Button
                  kind="primary"
                  type="submit"
                  disabled={isSubmitting || (isRegisterMode && !username.trim())}
                >
                  {isSubmitting
                    ? (isRegisterMode ? 'Registracija...' : 'Generisanje...')
                    : (isRegisterMode ? 'Registruj' : 'Generiši kod')}
                </Button>
              </div>
            </form>
          ) : (
            <div className="emp-account-result">
              <div className="emp-account-result__icon">
                <Icons.CheckCircle />
              </div>
              <h4 className="emp-account-result__title">
                {isRegisterMode ? 'Nalog uspešno registrovan' : 'Novi sigurnosni kod'}
              </h4>
              <p className="emp-account-result__subtitle">
                {isRegisterMode
                  ? 'Podelite sigurnosni kod sa zaposlenim. Važi 10 minuta.'
                  : 'Stara lozinka je poništena. Podelite novi kod sa zaposlenim. Važi 10 minuta.'}
              </p>

              <div className="emp-account-result__username">
                <span className="emp-account-result__label">Korisničko ime</span>
                <span className="emp-account-result__value">{result.username}</span>
              </div>

              <div className="emp-account-code">
                <span className="emp-account-code__label">Sigurnosni kod</span>
                <div className="emp-account-code__box">
                  <span className="emp-account-code__value">{result.secureCode}</span>
                  <button
                    type="button"
                    className="emp-account-code__copy"
                    onClick={handleCopy}
                    aria-label="Kopiraj kod"
                    title="Kopiraj kod"
                  >
                    {copied ? <Checkmark size={18} /> : <Copy size={18} />}
                  </button>
                </div>
                {copied && (
                  <span className="emp-account-code__copied">Kod je kopiran!</span>
                )}
              </div>

              <div className="emp-account-result__expiry">
                <Icons.Clock />
                <span>
                  Ističe u <strong>{formatExpiry(result.secureCodeExpiresAt)}</strong>
                </span>
              </div>

              <div className="emp-account-result__actions">
                <Button
                  kind="secondary"
                  onClick={onClose}
                  style={{ flex: 1 }}
                >
                  Zatvori
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ===========================================
// Deactivate Sidebar Component
// (Right side panel with a double confirmation for deactivating an employee's
//  user account — same workflow as the resident deactivation)
// ===========================================
const DeactivateEmployeeSidebar = ({ isOpen, onClose, employee, setNotification, onSuccess }) => {
  const { theme } = useTheme();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0); // 0 = details, 1 = confirmation summary

  // Reset form when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setStep(0);
    }
  }, [isOpen]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const fullName = employee?.fullName || `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim();
  const role = employee?.employeeTypeName || employee?.role || 'Zaposleni';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!employee?.id) {
      setError('Nedostaju podaci o zaposlenom');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      // Deaktivacija naloga zaposlenog (POST /api/Employee/{id}/deactivate):
      // gasi nalog, opoziva sve refresh tokene (odjava sa svih uređaja),
      // briše jednokratni sigurnosni kod i odmah odbija postojeće access tokene.
      await axiosInstance.post(`/api/Employee/${employee.id}/deactivate`);

      setNotification({
        kind: 'success',
        title: 'Uspešno deaktiviran',
        subtitle: 'Zaposleni je deaktiviran i odjavljen sa svih uređaja',
      });
      setTimeout(() => setNotification(null), 4000);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Greška pri deaktivaciji zaposlenog:', err);
      setError(err.response?.data?.message || 'Došlo je do greške pri deaktivaciji zaposlenog');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="emp-edit-sidebar-overlay"
          onClick={() => {
            if (!isSaving) onClose();
          }}
        />
      )}

      {/* Sidebar Panel */}
      <div className={`emp-edit-sidebar ${isOpen ? 'open' : ''} ${theme === 'g100' ? 'emp-page--dark' : ''}`}>
        <div className="emp-edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            {step === 0 ? 'Deaktivacija zaposlenog' : 'Potvrda deaktivacije'}
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={() => {
              if (!isSaving) onClose();
            }}
          />
        </div>

        <div className="emp-edit-sidebar__content">
          {step === 0 ? (
            <form className="emp-edit-sidebar__form" onSubmit={(e) => { e.preventDefault(); setStep(1); }}>
              <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: 'var(--cds-text-secondary, #525252)' }}>
                Da li ste sigurni da želite da deaktivirate zaposlenog{' '}
                <strong style={{ color: 'var(--cds-text-primary, #161616)' }}>{fullName}</strong>?
                Deaktiviran zaposleni biće odmah odjavljen sa svih uređaja i više neće moći da pristupi sistemu.
              </p>

              {error && (
                <div style={{ color: '#da1e28', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(218,30,40,0.08)', borderRadius: '4px', borderLeft: '3px solid #da1e28' }}>
                  {error}
                </div>
              )}

              <div className="emp-edit-sidebar__actions">
                <Button
                  kind="secondary"
                  onClick={onClose}
                  disabled={isSaving}
                >
                  Otkaži
                </Button>
                <Button
                  kind="danger"
                  type="submit"
                  disabled={isSaving}
                >
                  Dalje
                </Button>
              </div>
            </form>
          ) : (
            <form className="emp-edit-sidebar__form" onSubmit={handleSubmit}>
              {/* Warning summary */}
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(218, 30, 40, 0.08)',
                border: '1px solid rgba(218, 30, 40, 0.3)',
                borderLeft: '4px solid #da1e28',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: '600', color: '#da1e28' }}>
                  ⚠️ Deaktivacija stupa na snagu odmah!
                </p>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  Deaktivacijom zaposlenog <strong>{fullName}</strong>:
                </p>
                <ul style={{ margin: '0 0 0.5rem', paddingLeft: '1.25rem', fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  <li>Zaposleni se <strong>odjavljuje sa svih uređaja</strong></li>
                  <li>Zaposleni <strong>više ne može da se prijavi</strong> (lozinka se poništava)</li>
                  <li>Zaposleni <strong>gubi pristup svim delovima sistema</strong></li>
                </ul>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--cds-text-secondary, #525252)' }}>
                  Deaktivacija se sprovodi odmah, bez čekanja da isteknu postojeće sesije.
                </p>
              </div>

              {/* Summary of entered details */}
              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--cds-layer-02, #f4f4f4)',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--cds-text-primary, #161616)' }}>
                  Pregled deaktivacije
                </p>
                <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  <strong>Zaposleni:</strong> {fullName}
                </p>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  <strong>Tip zaposlenog:</strong> {role}
                </p>
              </div>

              {error && (
                <div style={{ color: '#da1e28', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(218,30,40,0.08)', borderRadius: '4px', borderLeft: '3px solid #da1e28' }}>
                  {error}
                </div>
              )}

              <div className="emp-edit-sidebar__actions">
                <Button
                  kind="secondary"
                  onClick={() => setStep(0)}
                  disabled={isSaving}
                >
                  Nazad
                </Button>
                <Button
                  kind="danger"
                  type="submit"
                  disabled={isSaving}
                >
                  {isSaving ? 'Deaktivacija...' : 'Potvrdi deaktivaciju'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};



// ===========================================
// Main EmployeeProfile Component
// ===========================================
const EmployeeProfile = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { user } = useAuth();

  // Efektivne dozvole prijavljenog korisnika (iz PermissionContext-a).
  // `authPermsLoading` = da li se dozvole još učitavaju (izbegavamo prikaz
  // pogrešnog podrazumevanog taba dok ne stignu).
  const { can, isAdmin, loading: authPermsLoading } = usePermissions();

  // Ceo profil zaposlenog vide administrator i korisnik sa `employees.manage`.
  // Korisniku kome je dodeljeno SAMO upravljanje smenama (`shifts.manage`,
  // "Dodavanje/brisanje smena za zaposlene") ostaje isključivo tab „Smene".
  const canViewFullProfile = isAdmin || can('employees.manage');
  const canViewShifts = isAdmin || can('shifts.manage');

  // Tabovi vidljivi trenutnom korisniku.
  const visibleTabs = useMemo(() => {
    if (canViewFullProfile) return TABS;
    return TABS.filter((tab) => tab.id === 'shifts' && canViewShifts);
  }, [canViewFullProfile, canViewShifts]);

  const tabStorageKey = `employee-tab:${user?.sub || user?.nameid || 'anonymous'}:${id}`;
  const getInitialTab = () => {
    // URL parametar `?tab=` ima prednost (npr. dolazak sa dashboarda na tab "Smene")
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && visibleTabs.some((t) => t.id === tabFromUrl)) return tabFromUrl;
    try {
      const saved = localStorage.getItem(tabStorageKey);
      if (saved && visibleTabs.some((t) => t.id === saved)) return saved;
    } catch (e) { /* ignore */ }
    return visibleTabs[0]?.id || 'general';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Ako se `?tab=` promeni dok je profil otvoren, prebaci na taj tab (samo ako
  // je tab dozvoljen trenutnom korisniku).
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && visibleTabs.some((t) => t.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, visibleTabs]);

  // Ako je aktivni tab sakriven (npr. dozvole su stigle nakon montiranja
  // komponente) - prebaci na prvi vidljivi tab.
  useEffect(() => {
    if (authPermsLoading) return;
    if (visibleTabs.length > 0 && !visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab, authPermsLoading]);

  useEffect(() => {
    try {
      localStorage.setItem(tabStorageKey, activeTab);
    } catch (e) { /* ignore */ }
  }, [activeTab, tabStorageKey]);

  const [employee, setEmployee] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shiftsError, setShiftsError] = useState(null);
  const [notification, setNotification] = useState(null);
  const tabListRef = useRef(null);
  const prevTabIndexRef = useRef(visibleTabs.findIndex((t) => t.id === activeTab));

  // On mobile, scroll the active tab into view. When navigating to the right,
  // the active tab aligns to the left edge; when navigating to the left, it
  // aligns to the right edge (opposite direction). Matches the resident profile tabs.
  useEffect(() => {
    const listEl = tabListRef.current;
    if (!listEl) return;
    const activeTabEl = listEl.querySelector('.emp-tab--active');
    if (!activeTabEl) return;

    const currentIndex = visibleTabs.findIndex((t) => t.id === activeTab);
    const prevIndex = prevTabIndexRef.current;
    const goingRight = currentIndex >= prevIndex;

    // Use getBoundingClientRect for precise, ancestor-independent positioning.
    const listRect = listEl.getBoundingClientRect();
    const tabRect = activeTabEl.getBoundingClientRect();

    let targetScrollLeft;
    if (goingRight) {
      // Align the active tab's left edge to the container's left edge.
      targetScrollLeft = listEl.scrollLeft + (tabRect.left - listRect.left);
    } else {
      // Align the active tab's right edge to the container's right edge.
      targetScrollLeft = listEl.scrollLeft + (tabRect.right - listRect.right);
    }

    listEl.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    prevTabIndexRef.current = currentIndex;
  }, [activeTab, visibleTabs]);

  // Edit state
  const [isEditSidebarOpen, setIsEditSidebarOpen] = useState(false);
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [editSection, setEditSection] = useState('identity');
  const [employeeTypes, setEmployeeTypes] = useState([]);

  // Image modal state
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Scheduling state
  const [selectedDates, setSelectedDates] = useState([]);
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedShiftType, setSelectedShiftType] = useState(null);
  const [isCreatingShifts, setIsCreatingShifts] = useState(false);
  const [schedulingStep, setSchedulingStep] = useState(0); // 0: select shift type, 1: creating

  // Delete shift state
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [isDeletingShift, setIsDeletingShift] = useState(false);

  // Selected day details (right side bar)
  const [selectedDay, setSelectedDay] = useState(null);

  // Overflow menu (three-dots) state
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const overflowMenuRef = useRef(null);

  // Deaktiviran ("bivši") zaposleni je SAMO ZA ČITANJE: sve izmene su
  // onemogućene (izmena podataka, nalozi, smene, dozvole).
  const isInactive = employee?.isActive === false;

  // Poruka koja se prikazuje pri pokušaju izmene deaktiviranog zaposlenog.
  const notifyReadOnly = useCallback(() => {
    setNotification({
      kind: 'info',
      title: 'Zaposleni je deaktiviran',
      subtitle: 'Podaci bivšeg zaposlenog su samo za čitanje.',
    });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  // Deactivate employee state
  // (Deaktivacija naloga zaposlenog - dvostruka potvrda u desnom panelu)
  const [isDeactivateEmployeeModalOpen, setIsDeactivateEmployeeModalOpen] = useState(false);

  // Account (Nalog) state
  const [isAccountSidebarOpen, setIsAccountSidebarOpen] = useState(false);
  const [accountMode, setAccountMode] = useState('register'); // 'register' | 'reset'
  const [isAccountSubmitting, setIsAccountSubmitting] = useState(false);
  const [accountResult, setAccountResult] = useState(null); // { secureCode, secureCodeExpiresAt, username }
  const [isCodeCopied, setIsCodeCopied] = useState(false);

  // Permissions (Dozvole) state
  const [permissions, setPermissions] = useState(null); // { userId, effective, overrides }
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState(null);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployee = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get(`/api/Employee/${id}`);
        const data = response.data?.data || response.data;
        setEmployee(data);
      } catch (err) {
        console.error('Greška pri učitavanju zaposlenog:', err);
        // Fallback: try to find in the list
        try {
          const listResponse = await axiosInstance.get('/api/Employee');
          const list = Array.isArray(listResponse.data)
            ? listResponse.data
            : (listResponse.data?.data || listResponse.data?.value || listResponse.data?.$values || []);
          const found = list.find((e) => e.id === id);
          if (found) {
            setEmployee(found);
          } else {
            setError('Zaposleni nije pronađen.');
          }
        } catch (listErr) {
          setError('Nije moguće učitati zaposlenog.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [id]);

  // Fetch permissions for this employee (GET /api/Permission/{userId})
  useEffect(() => {
    const fetchPermissions = async () => {
      if (activeTab !== 'permissions') return;
      if (!id || employee?.isRegistered !== true) return;
      setPermissionsLoading(true);
      setPermissionsError(null);
      try {
        const response = await axiosInstance.get(`/api/Permission/users/${id}`);
        const data = response.data?.data ?? response.data;
        setPermissions(data || null);
      } catch (err) {
        console.error('Greška pri učitavanju dozvola:', err);
        setPermissionsError(err.response?.data?.message || 'Nije moguće učitati dozvole.');
        setPermissions(null);
      } finally {
        setPermissionsLoading(false);
      }
    };
    fetchPermissions();
  }, [activeTab, id, employee?.isRegistered]);

  // Panel za zakazivanje smena i izbor datuma pripadaju tabu „Smene". Ako se
  // pređe na neki drugi tab, tok zakazivanja se napušta: panel se zatvara, a
  // izbor datuma se čisti. Time dugme „Zakaži smene" ne ostaje da „lebdi" nad
  // sadržajem drugih tabova, niti se vraća pri povratku na tab „Smene".
  useEffect(() => {
    if (activeTab === 'shifts') return;
    setIsSchedulingOpen(false);
    setSchedulingStep(0);
    setSelectedShiftType(null);
    setSelectedArea(null);
    setSelectedDates([]);
  }, [activeTab]);

  // Save permissions for this employee (PUT /api/Permission/users/{id})
  const handleSavePermissions = useCallback(async (draft) => {
    if (!id) return;
    if (isInactive) { notifyReadOnly(); return; }
    setIsSavingPermissions(true);
    try {
      // Backend očekuje listu dozvola: [{ permission, isAllowed }]
      const permissionsPayload = Object.entries(draft || {}).map(([permission, isAllowed]) => ({
        permission,
        isAllowed: isAllowed === true,
      }));
      const response = await axiosInstance.put(`/api/Permission/users/${id}`, {
        permissions: permissionsPayload,
      });
      if (response.data?.success === false) {
        throw new Error(response.data?.message || 'Greška pri čuvanju dozvola');
      }
      const data = response.data?.data ?? response.data;
      // Ako backend vrati osvežene dozvole (niz `effective`), iskoristi ih;
      // inače zadrži poslato stanje.
      // U oba slučaja zadrži ranije poznate labele - PUT odgovor ih često
      // ne sadrži, pa bi se inače prikazali sirovi ključevi dozvola.
      if (data && Array.isArray(data.effective)) {
        setPermissions((prev) => ({
          ...data,
          effective: mergeEffectiveLabels(data.effective, prev?.effective),
        }));
      } else {
        setPermissions((prev) => ({
          ...(prev || {}),
          effective: mergeEffectiveLabels(
            Object.entries(draft || {}).map(([permission, isAllowed]) => ({
              permission,
              isAllowed: isAllowed === true,
            })),
            prev?.effective
          ),
        }));
      }
      setNotification({
        kind: 'success',
        title: 'Dozvole sačuvane',
        subtitle: 'Dozvole pristupa su uspešno ažurirane.',
      });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      console.error('Greška pri čuvanju dozvola:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Došlo je do greške pri čuvanju dozvola.',
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsSavingPermissions(false);
    }
  }, [id, isInactive, notifyReadOnly]);

  // Fetch employee types (for the edit form dropdown)
  useEffect(() => {
    const fetchEmployeeTypes = async () => {
      try {
        const response = await axiosInstance.get('/api/EmployeeType');
        const data = Array.isArray(response.data)
          ? response.data
          : (response.data?.data || response.data?.value || response.data?.$values || []);
        setEmployeeTypes(data);
      } catch (err) {
        console.error('Greška pri učitavanju tipova zaposlenih:', err);
      }
    };
    fetchEmployeeTypes();
  }, []);

  // Handle edit sidebar save (PUT /api/Employee/{id})
  const handleEditSave = useCallback(async (formData) => {
    if (isInactive) { notifyReadOnly(); return; }
    setIsEditSaving(true);
    try {
      const payload = {
        id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        address: formData.address,
        hireDate: formData.hireDate ? new Date(formData.hireDate).toISOString() : null,
        employeeTypeId: formData.employeeTypeId,
        imgSrc: formData.imgSrc,
      };

      const response = await axiosInstance.put(`/api/Employee/${id}`, payload);

      if (response.data?.success) {
        setEmployee(prev => ({
          ...prev,
          ...payload,
          fullName: `${payload.firstName} ${payload.lastName}`.trim(),
          employeeTypeName: employeeTypes.find(t => t.id === payload.employeeTypeId)?.name || prev?.employeeTypeName,
        }));
        setIsEditSidebarOpen(false);
        setNotification({
          kind: 'success',
          title: 'Uspešno sačuvano',
          subtitle: 'Podaci zaposlenog su uspešno ažurirani',
        });
        setTimeout(() => setNotification(null), 3000);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        throw new Error(response.data?.message || 'Greška pri čuvanju podataka');
      }
    } catch (err) {
      console.error('Greška pri čuvanju zaposlenog:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Došlo je do greške pri čuvanju podataka',
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsEditSaving(false);
    }
  }, [id, employeeTypes, isInactive, notifyReadOnly]);

  // Open account sidebar in register mode
  const openRegisterSidebar = () => {
    if (isInactive) { notifyReadOnly(); return; }
    setAccountMode('register');
    setIsAccountSidebarOpen(true);
  };

  // Open account sidebar in reset mode
  const openResetSidebar = () => {
    if (isInactive) { notifyReadOnly(); return; }
    setAccountMode('reset');
    setIsAccountSidebarOpen(true);
  };

  // Register employee account (POST /api/Auth/register/employee/{id})
  const handleRegisterEmployee = useCallback(async (username) => {
    if (isInactive) { notifyReadOnly(); return null; }
    setIsAccountSubmitting(true);
    try {
      const response = await axiosInstance.post(`/api/Auth/register/employee/${id}`, {
        username,
      });
      if (response.data?.success) {
        const data = response.data.data;
        setEmployee(prev => ({
          ...prev,
          isRegistered: true,
          username: data?.username || prev?.username,
        }));
        setAccountResult(data);
        setNotification({
          kind: 'success',
          title: 'Nalog uspešno registrovan',
          subtitle: `Korisničko ime: ${data?.username || username}. Podelite sigurnosni kod sa zaposlenim.`,
        });
        setTimeout(() => setNotification(null), 6000);
        return data;
      }
      throw new Error(response.data?.message || 'Greška pri registraciji naloga');
    } catch (err) {
      console.error('Greška pri registraciji naloga:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Došlo je do greške pri registraciji naloga',
      });
      setTimeout(() => setNotification(null), 4000);
      return null;
    } finally {
      setIsAccountSubmitting(false);
    }
  }, [id, isInactive, notifyReadOnly]);

  // Reset employee password (POST /api/Auth/register/employee/{id}/regenerate-code)
  const handleResetPassword = useCallback(async () => {
    if (isInactive) { notifyReadOnly(); return null; }
    setIsAccountSubmitting(true);
    try {
      const response = await axiosInstance.post(`/api/Auth/register/employee/${id}/regenerate-code`);
      if (response.data?.success) {
        const data = response.data.data;
        setAccountResult(data);
        setNotification({
          kind: 'success',
          title: 'Novi sigurnosni kod',
          subtitle: 'Stara lozinka je poništena. Podelite novi kod sa zaposlenim.',
        });
        setTimeout(() => setNotification(null), 6000);
        return data;
      }
      throw new Error(response.data?.message || 'Greška pri generisanju koda');
    } catch (err) {
      console.error('Greška pri resetu lozinke:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Došlo je do greške pri resetu lozinke',
      });
      setTimeout(() => setNotification(null), 4000);
      return null;
    } finally {
      setIsAccountSubmitting(false);
    }
  }, [id, isInactive, notifyReadOnly]);

  // Copy the generated secure code to clipboard
  const handleCopyAccountCode = useCallback(async () => {
    if (!accountResult?.secureCode) return;
    try {
      await navigator.clipboard.writeText(accountResult.secureCode);
      setIsCodeCopied(true);
      setTimeout(() => setIsCodeCopied(false), 2000);
    } catch (err) {
      console.error('Greška pri kopiranju koda:', err);
    }
  }, [accountResult]);

  // Fetch shifts for this employee
  const fetchShifts = useCallback(async () => {
    if (!id) return;
    setShiftsLoading(true);
    setShiftsError(null);
    try {
      const response = await axiosInstance.get(`/api/Shift/employee/${id}`);
      if (response.data?.success) {
        setShifts(response.data.data || []);
      } else {
        setShifts([]);
      }
    } catch (err) {
      console.error('Greška pri učitavanju smena:', err);
      setShiftsError('Nije moguće učitati smene.');
      setShifts([]);
    } finally {
      setShiftsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const fetchShiftTypes = async () => {
    try {
      const response = await axiosInstance.get('/api/ShiftType');
      if (response.data?.success) {
        setShiftTypes(response.data.data.shiftTypesByArea || {});
      } else if (response.data?.data?.shiftTypesByArea) {
        setShiftTypes(response.data.data.shiftTypesByArea);
      }
    } catch (err) {
      console.error('Greška pri učitavanju tipova smena:', err);
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await axiosInstance.get('/api/Area');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.data || response.data?.value || response.data?.$values || []);
      setAreas(
        data.map((area) => ({
          id: area.id || area,
          label: area.name || area,
        }))
      );
    } catch (err) {
      console.error('Greška pri učitavanju područja:', err);
    }
  };

  const handleToggleDate = (date) => {
    if (isInactive) { notifyReadOnly(); return; }
    const dateKey = formatDateKey(date);
    setSelectedDates((prev) => {
      const exists = prev.some((d) => formatDateKey(d) === dateKey);
      if (exists) {
        return prev.filter((d) => formatDateKey(d) !== dateKey);
      }
      return [...prev, date];
    });
  };

  const openSchedulingPanel = () => {
    if (isInactive) { notifyReadOnly(); return; }
    setSelectedShiftType(null);
    setSelectedArea(null);
    setSchedulingStep(0);
    setIsSchedulingOpen(true);
    fetchShiftTypes();
    fetchAreas();
  };

  const closeSchedulingPanel = () => {
    if (isCreatingShifts) return;
    setIsSchedulingOpen(false);
    setSelectedShiftType(null);
    setSelectedArea(null);
    setSchedulingStep(0);
  };

  const handleScheduleShifts = async () => {
    if (isInactive) { notifyReadOnly(); return; }
    if (!selectedShiftType) {
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: 'Molimo izaberite tip smene',
      });
      return;
    }

    setSchedulingStep(1);
    setIsCreatingShifts(true);

    try {
      const formattedDates = selectedDates.map((date) => formatDateKey(date));
      const response = await axiosInstance.post('/api/Shift', {
        shiftTypeId: selectedShiftType,
        employeeId: id,
        dates: formattedDates,
      });

      if (response.data?.success) {
        setIsSchedulingOpen(false);
        setSelectedDates([]);
        setSelectedShiftType(null);
        setSchedulingStep(0);
        setNotification({
          kind: 'success',
          title: 'Uspeh',
          subtitle: 'Smene su uspešno zakazane!',
        });
        fetchShifts();
      } else {
        throw new Error(response.data?.message || 'Greška pri kreiranju smena');
      }
    } catch (err) {
      console.error('Greška pri kreiranju smena:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: getApiErrorMessage(err, 'Greška pri kreiranju smena'),
      });
      setSchedulingStep(0);
    } finally {
      setIsCreatingShifts(false);
    }
  };

  const handleDeleteShift = async () => {
    if (!shiftToDelete) return;
    if (isInactive) { notifyReadOnly(); return; }

    setIsDeletingShift(true);
    try {
      const response = await axiosInstance.delete(`/api/Shift/${shiftToDelete.id}`);

      if (response.data?.success) {
        setNotification({
          kind: 'success',
          title: 'Uspeh',
          subtitle: 'Smena je uspešno obrisana',
        });
        setShiftToDelete(null);
        fetchShifts();
      } else {
        throw new Error(response.data?.message || 'Smena ne može biti obrisana');
      }
    } catch (err) {
      console.error('Greška pri brisanju smene:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: getApiErrorMessage(err, 'Smena ne postoji ili ne može biti obrisana'),
      });
      setShiftToDelete(null);
    } finally {
      setIsDeletingShift(false);
    }
  };

  // Close overflow menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target)) {
        setIsOverflowMenuOpen(false);
      }
    };

    if (isOverflowMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOverflowMenuOpen]);

  // Handle successful deactivation: refresh the profile so the new account
  // status (isActive = false) is shown immediately.
  const handleEmployeeDeactivated = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axiosInstance.get(`/api/Employee/${id}`);
      const data = response.data?.data || response.data;
      if (data) {
        setEmployee(data);
      }
    } catch (err) {
      console.error('Greška pri osvežavanju zaposlenog nakon deaktivacije:', err);
    }
  }, [id]);

  const handleTabSelect = (tabId) => {
    // Ignoriši izbor taba koji korisniku nije dozvoljen.
    if (!visibleTabs.some((t) => t.id === tabId)) return;
    setActiveTab(tabId);
  };

  const handleTabKeyDown = (e, index) => {
    const tabs = visibleTabs;
    let newIndex = index;
    if (e.key === 'ArrowRight') {
      newIndex = (index + 1) % tabs.length;
    } else if (e.key === 'ArrowLeft') {
      newIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (e.key === 'Home') {
      newIndex = 0;
    } else if (e.key === 'End') {
      newIndex = tabs.length - 1;
    } else {
      return;
    }
    e.preventDefault();
    handleTabSelect(tabs[newIndex].id);
    const tabButtons = tabListRef.current?.querySelectorAll('.emp-tab');
    tabButtons?.[newIndex]?.focus();
  };

  const renderTabContent = () => {
    // Ako aktivni tab nije dozvoljen trenutnom korisniku (npr. korisnik sa
    // samo `shifts.manage`), prikaži prvi dozvoljeni tab umesto podrazumevanog.
    const tabId = visibleTabs.some((t) => t.id === activeTab)
      ? activeTab
      : visibleTabs[0]?.id;
    if (!tabId) return null;
    switch (tabId) {
      case 'general':
        return (
          <GeneralInfo
            employee={employee}
            onEditSection={(section) => {
              if (isInactive) { notifyReadOnly(); return; }
              setEditSection(section);
              setIsEditSidebarOpen(true);
            }}
            onRegister={openRegisterSidebar}
            onResetPassword={openResetSidebar}
            isRegistering={isAccountSubmitting && accountMode === 'register'}
            isResetting={isAccountSubmitting && accountMode === 'reset'}
            accountResult={accountResult}
            onCopyCode={handleCopyAccountCode}
            copied={isCodeCopied}
            readOnly={isInactive}
          />
        );
      case 'permissions':
        return (
          <PermissionsTab
            employee={employee}
            permissions={permissions}
            loading={permissionsLoading}
            error={permissionsError}
            onRegister={openRegisterSidebar}
            isRegistering={isAccountSubmitting && accountMode === 'register'}
            onSave={handleSavePermissions}
            isSaving={isSavingPermissions}
            readOnly={isInactive}
          />
        );
      case 'shifts':
        return (
          <ShiftCalendar
            shifts={shifts}
            loading={shiftsLoading}
            error={shiftsError}
            selectedDates={selectedDates}
            onToggleDate={handleToggleDate}
            onDeleteShift={setShiftToDelete}
            onSelectDay={setSelectedDay}
            readOnly={isInactive}
          />
        );
      case 'tasks':
        return <EmployeeTasksTab employeeId={id} />;
      case 'activity':
        return (
          <PlaceholderTab
            icon={Icons.Activity}
            title="Aktivnost"
            description="Aktivnost zaposlenog će biti dostupna uskoro."
          />
        );
      default:
        return (
          <GeneralInfo
            employee={employee}
            onEditSection={(section) => {
              if (isInactive) { notifyReadOnly(); return; }
              setEditSection(section);
              setIsEditSidebarOpen(true);
            }}
            onRegister={openRegisterSidebar}
            onResetPassword={openResetSidebar}
            isRegistering={isAccountSubmitting && accountMode === 'register'}
            isResetting={isAccountSubmitting && accountMode === 'reset'}
            accountResult={accountResult}
            onCopyCode={handleCopyAccountCode}
            copied={isCodeCopied}
            readOnly={isInactive}
          />
        );
    }
  };

  // Loading state (podaci o zaposlenom i/ili dozvole prijavljenog korisnika)
  if (loading || authPermsLoading) {
    return (
      <div className={`emp-page ${theme === 'g100' ? 'emp-page--dark' : ''}`}>
        <div className="emp-container">
          {/* Header Skeleton */}
          <header className="emp-header emp-skeleton-header" role="banner" aria-hidden="true">
            <div className="emp-identity">
              <div className="emp-avatar-wrapper">
                <SkeletonPlaceholder className="emp-skeleton-avatar" />
              </div>
              <div className="emp-identity-content">
                <SkeletonText className="emp-skeleton-name" />
              </div>
            </div>
            <SkeletonPlaceholder className="emp-skeleton-edit-btn" />
          </header>

          {/* Tabs Skeleton */}
          <nav className="emp-tabs emp-skeleton-tabs" aria-hidden="true">
            <div className="emp-tabs__list">
              {TABS.map((tab) => (
                <SkeletonText key={tab.id} className="emp-skeleton-tab" />
              ))}
            </div>
          </nav>

          {/* Cards Skeleton */}
          <main className="emp-main" aria-hidden="true">
            <div className="emp-cards-grid">
              {[0, 1, 2].map((card) => (
                <div key={card} className="emp-card emp-skeleton-card">
                  <div className="emp-card__header">
                    <SkeletonText className="emp-skeleton-card-title" />
                    <SkeletonPlaceholder className="emp-skeleton-card-edit" />
                  </div>
                  <div className="emp-card__body">
                    <div className="emp-data-list">
                      {[0, 1, 2].map((row) => (
                        <div key={row} className="emp-data-item">
                          <SkeletonText className="emp-skeleton-label" />
                          <SkeletonText className="emp-skeleton-value" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !employee) {
    return (
      <div className={`emp-page ${theme === 'g100' ? 'emp-page--dark' : ''}`}>
        <div className="emp-container">
          <div className="emp-empty-state">
            <div className="emp-empty-icon"><Icons.User /></div>
            <h2 className="emp-empty-title">{error ? 'Greška' : 'Zaposleni nije pronađen'}</h2>
            <p className="emp-empty-description">
              {error || 'Ne možemo pronaći traženi profil zaposlenog.'}
            </p>
            <Link to="/employees" className="emp-btn emp-btn--primary" style={{ marginTop: '16px', display: 'inline-flex' }}>
              Nazad na listu
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const fullName = employee.fullName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim();

  return (
    <div className={`emp-page ${theme === 'g100' ? 'emp-page--dark' : ''}`}>
      {/* Floating Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '4rem',
          right: '1rem',
          zIndex: 10000,
          minWidth: '320px',
          animation: 'empSlideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <InlineNotification
            kind={notification.kind}
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={() => setNotification(null)}
            hideCloseButton={false}
            lowContrast
          />
        </div>
      )}

      <div className="emp-container">
        {/* Image Modal */}
        <ImageModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          imageSrc={employee.imgSrc}
          altText={`Fotografija: ${fullName}`}
        />

        {/* Edit Sidebar */}
        <EmployeeEditSidebar
          isOpen={isEditSidebarOpen}
          onClose={() => setIsEditSidebarOpen(false)}
          employee={employee}
          onSave={handleEditSave}
          isSaving={isEditSaving}
          employeeTypes={employeeTypes}
          section={editSection}
        />

        {/* Account Sidebar (Nalog) */}
        <AccountSidebar
          isOpen={isAccountSidebarOpen}
          onClose={() => setIsAccountSidebarOpen(false)}
          employee={employee}
          mode={accountMode}
          onRegister={handleRegisterEmployee}
          onResetPassword={handleResetPassword}
          isSubmitting={isAccountSubmitting}
        />

        {/* Header */}
        <header className="emp-header" role="banner">
          <div className="emp-identity">
            <div className="emp-avatar-wrapper">
              <EmployeeAvatar
                avatar={employee.imgSrc}
                fullName={fullName}
                clickable={!!employee.imgSrc}
                onClick={() => setIsImageModalOpen(true)}
              />
            </div>
            <div className="emp-identity-content">
              <div className="emp-identity-primary">
                <h1 className="emp-name">{fullName}</h1>
                {isInactive && <Tag type="red" size="sm">Deaktiviran</Tag>}
              </div>
            </div>
          </div>
          {/* Izmena/deaktivacija profila zahteva `employees.manage`; korisnik
              sa samo `shifts.manage` vidi isključivo tab „Smene" bez akcija. */}
          {canViewFullProfile && (
            <div className="emp-actions">
              {/* Three-dots overflow menu */}
              <OverflowMenu
                isOpen={isOverflowMenuOpen}
                onToggle={() => setIsOverflowMenuOpen(!isOverflowMenuOpen)}
                onClose={() => setIsOverflowMenuOpen(false)}
                menuRef={overflowMenuRef}
              >
                <OverflowMenuItem
                  icon={Icons.Pencil}
                  onClick={() => {
                    if (isInactive) { notifyReadOnly(); return; }
                    setIsOverflowMenuOpen(false);
                    setEditSection('all');
                    setIsEditSidebarOpen(true);
                  }}
                  disabled={isInactive}
                >
                  Izmeni profil
                </OverflowMenuItem>
                <OverflowMenuItem
                  icon={Icons.MinusCircle}
                  variant="danger"
                  onClick={() => {
                    setIsOverflowMenuOpen(false);
                    setIsDeactivateEmployeeModalOpen(true);
                  }}
                  disabled={isInactive}
                >
                  Deaktiviraj zaposlenog
                </OverflowMenuItem>
              </OverflowMenu>
            </div>
          )}
        </header>

        {/* Navigation Tabs */}
        <nav className="emp-tabs" aria-label="Navigacija profila zaposlenog">
          <div
            className="emp-tabs__list"
            role="tablist"
            ref={tabListRef}
            aria-label="Sekcije profila zaposlenog"
          >
            {visibleTabs.map((tab, index) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  id={`emp-tab-${tab.id}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`emp-panel-${tab.id}`}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  className={`emp-tab ${activeTab === tab.id ? 'emp-tab--active' : ''}`}
                  onClick={() => handleTabSelect(tab.id)}
                  onKeyDown={(e) => handleTabKeyDown(e, index)}
                >
                  <IconComponent />
                  {tab.label}
                  {tab.id === 'shifts' && shifts.length > 0 && (
                    <span className="emp-tab__badge">{shifts.length}</span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content Area */}
        <main
          id={`emp-panel-${activeTab}`}
          className="emp-main"
          role="tabpanel"
          aria-labelledby={`emp-tab-${activeTab}`}
        >
          {renderTabContent()}
        </main>
      </div>

      {/* Floating Schedule Button - prikazuje se samo na tabu „Smene", gde se
          datumi i biraju (sakriven za deaktivirane zaposlene). */}
      {!isInactive && activeTab === 'shifts' && selectedDates.length > 0 && (
        <button
          type="button"
          className="emp-floating-schedule-btn"
          onClick={openSchedulingPanel}
        >
          <Calendar size={24} style={{ fill: '#ffffff' }} />
          <span>Zakaži smene ({selectedDates.length})</span>
        </button>
      )}

      {/* Right Side Bar - Scheduling Panel (samo na tabu „Smene") */}
      {isSchedulingOpen && activeTab === 'shifts' && (
        <>
          <div
            className="emp-scheduling-overlay"
            onClick={closeSchedulingPanel}
          />
          <div className={`emp-scheduling-sidebar ${theme === 'g100' ? 'emp-scheduling-sidebar--dark' : ''}`}>
            <div className="emp-scheduling-sidebar__header">
              <div>
                <h3>Zakaži smene</h3>
                <p>
                  {selectedDates.length} {selectedDates.length === 1 ? 'datum' : 'datuma'} izabrano
                </p>
              </div>
              {!isCreatingShifts && (
                <button
                  type="button"
                  className="emp-scheduling-sidebar__close"
                  onClick={closeSchedulingPanel}
                  aria-label="Zatvori"
                >
                  <Close size={20} />
                </button>
              )}
            </div>

            <div className="emp-scheduling-sidebar__content">
              {schedulingStep === 0 && (
                <>
                  {/* Employee Info */}
                  <div className="emp-scheduling-employee">
                    <h4>Zaposleni</h4>
                    <p>{fullName}</p>
                  </div>

                  {/* Selected Dates */}
                  <div className="emp-scheduling-dates">
                    <h4>Izabrani datumi</h4>
                    <div className="emp-scheduling-dates__list">
                      {selectedDates.map((date, idx) => (
                        <Tag key={idx} type="blue" size="md">
                          {new Date(date).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          }).replace(/\//g, '.')}
                        </Tag>
                      ))}
                    </div>
                  </div>

                  {/* Area Selection */}
                  <div className="emp-scheduling-field">
                    <Dropdown
                      id="emp-area-dropdown"
                      titleText="Područje"
                      label="Izaberite područje"
                      items={areas}
                      itemToString={(item) => (item ? item.label : '')}
                      selectedItem={areas.find((a) => a.id === selectedArea) || null}
                      onChange={({ selectedItem }) => {
                        setSelectedArea(selectedItem?.id || null);
                        setSelectedShiftType(null);
                      }}
                    />
                  </div>

                  {/* Shift Type Selection */}
                  <div className="emp-scheduling-field">
                    <Dropdown
                      id="emp-shift-type-dropdown"
                      titleText="Tip smene"
                      label="Izaberite tip smene"
                      items={(selectedArea
                        ? (shiftTypes[areas.find((a) => a.id === selectedArea)?.label] || [])
                        : Object.values(shiftTypes).flat()
                      ).map((shift) => ({
                        id: shift.id,
                        label: `${shift.name} (${shift.startTime} - ${shift.formattedEndTime})`,
                      }))}
                      itemToString={(item) => (item ? item.label : '')}
                      selectedItem={(selectedArea
                        ? (shiftTypes[areas.find((a) => a.id === selectedArea)?.label] || [])
                        : Object.values(shiftTypes).flat()
                      ).map((shift) => ({
                        id: shift.id,
                        label: `${shift.name} (${shift.startTime} - ${shift.formattedEndTime})`,
                      })).find((s) => s.id === selectedShiftType) || null}
                      onChange={({ selectedItem }) => setSelectedShiftType(selectedItem?.id || null)}
                    />
                  </div>
                </>
              )}

              {schedulingStep === 1 && (
                <div className="emp-scheduling-progress">
                  {isCreatingShifts ? (
                    <>
                      <div className="emp-scheduling-spinner" />
                      <p>Zakazivanje smena u toku...</p>
                    </>
                  ) : (
                    <>
                      <div className="emp-scheduling-success">
                        <Checkmark size={32} style={{ fill: '#ffffff' }} />
                      </div>
                      <h4>Smene uspešno zakazane!</h4>
                      <p>
                        Kreirano je {selectedDates.length}{' '}
                        {selectedDates.length === 1 ? 'smena' : 'smena'}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {schedulingStep === 0 && (
              <div className="emp-scheduling-sidebar__footer">
                <Button kind="secondary" onClick={closeSchedulingPanel} style={{ flex: 1 }}>
                  Otkaži
                </Button>
                <Button
                  kind="primary"
                  onClick={handleScheduleShifts}
                  disabled={!selectedShiftType || isCreatingShifts || isInactive}
                  style={{ flex: 1, backgroundColor: '#9fb746', borderColor: '#9fb746' }}
                >
                  Zakaži smene
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Day Details Right Side Bar */}
      {selectedDay && (
        <>
          <div
            className="emp-scheduling-overlay"
            onClick={() => setSelectedDay(null)}
          />
          <div className={`emp-scheduling-sidebar ${theme === 'g100' ? 'emp-scheduling-sidebar--dark' : ''}`}>
            <div className="emp-scheduling-sidebar__header">
              <div>
                <h3>{formatDateLong(selectedDay.date)}</h3>
                <p>
                  {selectedDay.shifts.length}{' '}
                  {selectedDay.shifts.length === 1 ? 'smena' : 'smena'}
                </p>
              </div>
              <button
                type="button"
                className="emp-scheduling-sidebar__close"
                onClick={() => setSelectedDay(null)}
                aria-label="Zatvori"
              >
                <Close size={20} />
              </button>
            </div>

            <div className="emp-scheduling-sidebar__content">
              {selectedDay.shifts.length === 0 ? (
                <div className="emp-no-shifts-message">
                  <Time size={32} />
                  <p>Nema smena za ovaj dan</p>
                </div>
              ) : (
                <div className="emp-day-details-list">
                  {selectedDay.shifts.map((shift, idx) => (
                    <div key={idx} className="emp-day-detail-card">
                      <div className="emp-day-detail-card__header">
                        <h4>{shift.name}</h4>
                        <Tag size="sm" type={getStatusColor(shift.status)}>
                          {translateStatus(shift.status)}
                        </Tag>
                      </div>
                      <div className="emp-day-detail-card__row">
                        <Time size={16} />
                        <span className="emp-day-detail-card__label">Vreme:</span>
                        <span className="emp-day-detail-card__value">
                          {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                        </span>
                      </div>
                      <div className="emp-day-detail-card__row">
                        <Location size={16} />
                        <span className="emp-day-detail-card__label">Oblast:</span>
                        <span className="emp-day-detail-card__value">{shift.area || '-'}</span>
                      </div>
                      {shift.description && (
                        <div className="emp-day-detail-card__description">
                          <span className="emp-day-detail-card__label">Opis:</span>
                          <p>{shift.description}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Delete Shift Confirmation Modal */}
      {!isInactive && shiftToDelete && (
        <>
          <div
            className="emp-scheduling-overlay"
            onClick={() => {
              if (!isDeletingShift) setShiftToDelete(null);
            }}
          />
          <div className={`emp-delete-modal ${theme === 'g100' ? 'emp-delete-modal--dark' : ''}`}>
            <h4>Brisanje smene</h4>
            <p>Da li ste sigurni da želite da obrišete ovu smenu?</p>
            {shiftToDelete && (
              <div className="emp-delete-modal__info">
                <div className="emp-delete-modal__row">
                  <span className="emp-delete-modal__label">Tip smene:</span>
                  <span className="emp-delete-modal__value">{shiftToDelete.name}</span>
                </div>
                <div className="emp-delete-modal__row">
                  <span className="emp-delete-modal__label">Vreme:</span>
                  <span className="emp-delete-modal__value">
                    {formatTime(shiftToDelete.startTime)} - {formatTime(shiftToDelete.endTime)}
                  </span>
                </div>
                <div className="emp-delete-modal__row">
                  <span className="emp-delete-modal__label">Oblast:</span>
                  <span className="emp-delete-modal__value">{shiftToDelete.area || '-'}</span>
                </div>
              </div>
            )}
            <div className="emp-delete-modal__actions">
              <Button
                kind="secondary"
                onClick={() => setShiftToDelete(null)}
                disabled={isDeletingShift}
                style={{ flex: 1 }}
              >
                Otkaži
              </Button>
              <Button
                kind="danger"
                onClick={handleDeleteShift}
                disabled={isDeletingShift}
                style={{ flex: 1 }}
              >
                {isDeletingShift ? 'Brisanje...' : 'Obriši'}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Deactivate Employee Sidebar (double confirmation) */}
      <DeactivateEmployeeSidebar
        isOpen={isDeactivateEmployeeModalOpen}
        onClose={() => setIsDeactivateEmployeeModalOpen(false)}
        employee={employee}
        setNotification={setNotification}
        onSuccess={handleEmployeeDeactivated}
      />
    </div>
  );
};

export default EmployeeProfile;
